import React, { useState, useEffect } from "react";
import api from "../../api/api";
import WysiwygEditor from "../common/WysiwygEditor";

export default function CreatePublicationModal({
  onClose,
  onSuccess,
  setNotification,
  editData = null
}) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [type, setType] = useState("article");
  const [status, setStatus] = useState("rascunho");

  const [file, setFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(false);

  /* ======================================================
     EDIÇÃO
  ====================================================== */
  useEffect(() => {
    if (!editData) return;

    setTitle(editData.title || "");
    setContent(editData.content || "");
    setType(editData.type || "article");
    setStatus(editData.status || "rascunho");

    if (editData.media) {
      if (editData.media.type === "youtube") {
        setMediaUrl(editData.media.url);
      } else {
        setPreview(editData.media.url);
      }
    }
  }, [editData]);

  /* ======================================================
     SUBMIT
  ====================================================== */
  const handleSubmit = async () => {
    if (!title.trim()) {
      return setNotification({
        show: true,
        type: "warning",
        message: "O título é obrigatório"
      });
    }

    try {
      setLoading(true);

      const formData = new FormData();

      // CAMPOS BÁSICOS
      formData.append("title", title);
      formData.append("content", content); // 🔥 HTML do WYSIWYG
      formData.append("type", type);
      formData.append("status", status);

      // YOUTUBE
      if (type === "youtube") {
        formData.append("media_url", mediaUrl);
      }

      // NOVO UPLOAD
      if (file) {
        formData.append("file", file);
      }

      // PRESERVA MÍDIA EXISTENTE NA EDIÇÃO
      if (
        editData &&
        !file &&
        type !== "youtube" &&
        editData.media?.url
      ) {
        formData.append("media_url", editData.media.url);
        formData.append("media_type", editData.media.type);
      }

      // REQUEST
      if (editData) {
        await api.put(
          `/admin/publications/${editData.id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        await api.post(
          "/admin/publications",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      setNotification({
        show: true,
        type: "success",
        message: editData
          ? "✅ Publicação atualizada com sucesso"
          : "🎉 Publicação criada com sucesso"
      });

      if (onSuccess) onSuccess();
      onClose();

    } catch (err) {
      console.error("Erro ao salvar publicação:", err);

      setNotification({
        show: true,
        type: "error",
        message:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Erro ao salvar publicação"
      });
    } finally {
      setLoading(false);
    }
  };

  /* ======================================================
     FILE
  ====================================================== */
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  };

  const resetMedia = () => {
    setFile(null);
    setMediaUrl("");
    setPreview(null);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl animate-scaleUp overflow-hidden">

        {/* HEADER */}
        <div className="p-6 border-b flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-black text-gray-800">
            {editData ? "📝 Editar Publicação" : "📚 Nova Publicação"}
          </h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">

          {/* TÍTULO */}
          <div>
            <label className="text-sm font-bold">Título</label>
            <input
              className="w-full mt-1 border rounded-lg p-3 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* TYPE / STATUS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold">Tipo</label>
              <select
                className="w-full mt-1 border rounded-lg p-3 text-sm"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  resetMedia();
                }}
              >
                <option value="article">Artigo (Texto)</option>
                <option value="image">Imagem</option>
                <option value="pdf">PDF</option>
                <option value="video">Vídeo</option>
                <option value="youtube">YouTube</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-bold">Status</label>
              <select
                className="w-full mt-1 border rounded-lg p-3 text-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="rascunho">Rascunho</option>
                <option value="publicado">Publicado</option>
                <option value="arquivado">Arquivado</option>
              </select>
            </div>
          </div>

          {/* YOUTUBE */}
          {type === "youtube" && (
            <div>
              <label className="text-sm font-bold">Link do YouTube</label>
              <input
                className="w-full mt-1 border rounded-lg p-3 text-sm"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
          )}

          {/* UPLOAD */}
          {type !== "article" && type !== "youtube" && (
            <div className="border-2 border-dashed rounded-xl p-4 bg-gray-50 text-center">
              <input
                type="file"
                accept={
                  type === "pdf"
                    ? ".pdf"
                    : type === "image"
                    ? "image/*"
                    : "video/*"
                }
                onChange={handleFileChange}
              />

              {preview && (
                <div className="mt-4 rounded-xl overflow-hidden border bg-white">
                  {type === "image" && <img src={preview} className="max-h-64 mx-auto" />}
                  {type === "pdf" && <iframe src={preview} className="w-full h-64" />}
                  {type === "video" && <video src={preview} controls className="w-full max-h-64" />}
                </div>
              )}
            </div>
          )}

          {/* CONTEÚDO — WYSIWYG */}
          <div>
            <label className="text-sm font-bold mb-1 block">Conteúdo</label>

            <WysiwygEditor
              value={content}
              onChange={setContent}
              placeholder="Escreva o conteúdo da publicação..."
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl border bg-white font-bold"
          >
            Cancelar
          </button>
          <button
            disabled={loading}
            onClick={handleSubmit}
            className="px-8 py-2 rounded-xl bg-indigo-600 text-white font-bold disabled:opacity-50"
          >
            {loading ? "Salvando..." : editData ? "Atualizar" : "Publicar"}
          </button>
        </div>

      </div>
    </div>
  );
}
